import { defineStore } from "pinia";
import { CLD_ENV } from "~~/shared/utils/env";
import { useCldURLs } from "~~/shared/utils/apiv2.urls";
export const useCartStore = defineStore("cart", () => {
  const pending = ref(false);
  const cartItems = ref([]);
  const qtyCart = ref(0);
  const cartData = useState('cartData', () => ({
    items: [],
    amountExcludingVat: 0,
    amountIncludingVat: 0,
    vatRate: 0,
  }));
  const priceSingleQty = ref([]);
  const sessionData = ref({});
  const orderPlaceResponse = ref({});
  const showCartBtn = ref(false);

  // `silent`: skip the "refresh-cart" broadcast (bulk import adds many lines in a
  // row and emits a single refresh itself once done).
  const addToCart = async ({ sku, qty, silent = false }) => {
    pending.value = true;

    try {
      if (+qty > 10000) {
        qty = 10000;
      }

      let alreadyAdded;
      if (cartData.value?.items && cartData.value.items.length) {
        alreadyAdded = cartData.value.items.find((x) => x.articleId === sku);
      }

      if (!alreadyAdded) {
        const { data: added, error } = await addToCartHttp({
          sku: sku,
          qty,
        });

        if (error?.value) {
          const reason = describeCartError(error.value);
          console.error("[CartStore.addToCart] cart/add rejected", { sku, qty, reason, error: error.value });
          return { status: false, added: false, error: reason };
        }

        if (added?.value) {
          const exist = (added.value.items || []).find((x) => String(x.articleId).trim() === String(sku).trim());
          if (exist) {
            const sumQty = added.value.items
              .map((item) => +item.quantity)
              .reduce((prev, next) => prev + next);

            qtyCart.value = sumQty;
            cartData.value = added.value;
            if (!silent) EventBus.emit("refresh-cart");
            return { status: true, added: true };
          }
        }

        console.error("[CartStore.addToCart] cart/add answered without the line", { sku, qty, response: added?.value });
        return { status: false, added: false, error: "line_missing_in_response" };
      }

      // already exists in cart -> update qty
      let qtySum = qty + alreadyAdded.quantity;
      if (+qtySum > 10000) {
        qtySum = 10000;
      }
      await UpdateProductQty({ qty: qtySum, dlId: alreadyAdded.dlId });
      if (!silent) EventBus.emit("refresh-cart");
      return { status: true, added: false };
    } catch (error) {
      console.error("[CartStore.addToCart] failed", error);
      return { status: false, added: false, error: describeCartError(error) };
    } finally {
      pending.value = false;
    }
  };

  // "<status> <backend message>" for toasts/lists. The auth proxy re-throws the
  // backend status and puts the backend body under `data`, so prefer that.
  const describeCartError = (err) => {
    const status = err?.statusCode ?? err?.response?.status ?? err?.status;
    const body = err?.data?.data ?? err?.data?.message ?? err?.response?._data ?? err?.data;
    const text =
      typeof body === "string"
        ? body
        : body && typeof body === "object"
          ? body.label || body.message || body.title || JSON.stringify(body)
          : err?.statusMessage || err?.message || "";
    return [status, text].filter(Boolean).join(" ").trim() || "unknown_error";
  };
  const getFromCart = async () => {
    const data = await getCartHttp();
    /*
      if (data && data.value && data.value.Data) {
        cartData.value = data.value.Data;
        if (data.value.Data.items.length) {
          priceSingleQty.value = data.value.Data.items.map(
            ({ Quantity, Total }) => parseFloat(Total / Quantity).toFixed(2)
          );
          const sumQty = data.value.Data.items
            .map((item) => +item.Quantity)
            .reduce((prev, next) => prev + next);
          qtyCart.value = sumQty;
        }
      }
  
    */

    if (data && data.value) {
      cartData.value = data.value;
      if (data.value.items.length) {
        priceSingleQty.value = data.value.items.map(({ quantity, total }) =>
          parseFloat(total / quantity).toFixed(2),
        );
        const sumQty = data.value.items
          .map((item) => +item.quantity)
          .reduce((prev, next) => prev + next);
        qtyCart.value = sumQty;
      }
    }
    return data;
  };
  const UpdateProductQty = async ({ qty, dlId }) => {
    if (+qty > 10000) {
      qty = 10000;
    }
    const data = await UpdateQtyInCartHttp({ qty, dlId });
    // console.log("updated qty in cart", data.data);

    if (!data.value) {
      useShowValidationToasts({
        Cart: ["Cart operation error."],
      });
      getFromCart();
      return;
    }
    cartData.value = data.value;
    const sumQty = data.value.items
      .map((item) => +item.quantity)
      .reduce((prev, next) => prev + next);
    qtyCart.value = sumQty;
  };
  const removeLineCart = async ({ dlId }) => {
    const data = await removeFromCartHttp({ dlId });

    cartData.value = data.value;
    const sumQty = data.value.items.length
      ? data.value.items
          .map((item) => +item.quantity)
          .reduce((prev, next) => prev + next)
      : 0;

    qtyCart.value = sumQty;
  };
  const placeOrder = async ({ reference }) => {
    const data = await placeOrderHttp({ reference });
    orderPlaceResponse.value = data.value;
    if (orderPlaceResponse.value.status) {
      cartData.value = { items: [] };
      qtyCart.value = 0;
    }
    console.log("data CL Order place", data);
  };

  const setCart = async ({ cart, qty }) => {
    cartData.value = cart;
    qtyCart.value = qty;
  };

  const resetCartStates = async () => {
    cartData.value = {
      items: [],
      amountExcludingVat: 0,
      amountIncludingVat: 0,
      vatRate: 0,
    };

    qtyCart.value = 0;
  };

  const resetCart = async () => {
    const data = await resetUserCartHttp();

    if (data.value && data.value.status) {
      cartData.value = { items: [] };
      qtyCart.value = 0;
    }
  };

  const showAddCartBtn = (value) => {
    showCartBtn.value = value;
  };
  const clearCartResponse = () => {
    orderPlaceResponse.value = {};
  };
  //#region http functions
  const addToCartHttp = async ({ sku, qty }) => {
    const payload = {
      sku: sku,
      qty: +qty,
    };
    const url = useCldURLs[CLD_ENV].cart.add;
    const { data, error } = await useFetchApi(
      url,
      {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      },
      true,
    );

    return { data, error };
  };

  const UpdateQtyInCartHttp = async ({ qty, dlId }) => {
    const url = `${useCldURLs[CLD_ENV].cart.update}`;
    const { data } = await useFetchApi(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ qty, dlId }),
    });
    return data;
  };

  const getCartHttp = async () => {
    const isAuthenticated = useCookie("isAuthenticated").value;
    if (!isAuthenticated) {
      return false;
    }
    const url = useCldURLs[CLD_ENV].cart.get;

    const { data } = await useFetchApi(url, {
      method: "GET",
      key: `get-cart-${new Date().getTime()}`, // prevent cache
      headers: {
        "Content-Type": "application/json",
      },
    });
    return data;
  };

  const removeFromCartHttp = async ({ dlId }) => {
    const url = `${useCldURLs[CLD_ENV].cart.remove}?dlId=${dlId}`;
    const { data } = await useFetchApi(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    return data;
  };

  const resetUserCartHttp = async () => {
    const url = useCldURLs[CLD_ENV].cart.reset;

    const { data } = await useFetchApi(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    return data;
  };

  const placeOrderHttp = async ({ reference }) => {
    const url = useCldURLs[CLD_ENV].order.place;

    const { data } = await useFetchApi(`${url}?reference=${reference}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return data;
  };
  const syncCartQty = (qty) => {
    qtyCart.value = qty;
  };
  //#endregion
  return {
    cartItems,
    priceSingleQty,
    sessionData,
    cartData,
    showCartBtn,
    orderPlaceResponse,
    qtyCart,
    addToCart,
    setCart,
    showAddCartBtn,
    getFromCart,
    UpdateProductQty,
    removeLineCart,
    placeOrder,
    resetCart,
    resetCartStates,
    clearCartResponse,
    syncCartQty,
  };
});
